import config from "@Config/index";
import { AppThunk } from "@Store/index";
import axios from "axios";
import { getRegionListError, getRegionListSuccess, updateListingData } from "./searchPageReducer";
import {
  getSearchMetaTitle,
  getSearchMetaDescription,
  getSearchCanonicalUrl,
  getSearchLink,
  getSearchRobotsTag,
} from "./helper";
import { getMetaSuccess } from "@App/appReducer";
import urljoin from "helpers/urljoin";
import fetchStateLocalities from "../components/SectionRegionElements/components/CountryStateSection/data/fetchStateLocalities";
import fetchStateAreas from "../components/SectionRegionElements/components/CountryStateSection/data/fetchStateAreas";
import fetchStateUniversities from "../components/SectionRegionElements/components/CountryStateSection/data/fetchStateUniversities";

const fetchRegionListData =
  ({ region, canonical_name, selectedLang }: any): AppThunk =>
  async (dispatch: any) => {
    try {
      // Always fetch localities/sublocalities/establishments
      const { data } = await axios(`${config.API_URL}/api/v0/regions`, {
        params: {
          p: 1,
          limit: 1000,
          state: "active",
          methods: "parent_name",
          only: "name,canonical_name,region_type",
          ancestor_id: region.id,
          sort_key: "name",
          sort_order: "asc",
          region_types: "locality,sublocality,establishment",
          min_active_inventory_count_in_region: 1,
          use_java_api: true,
        },
        ...config.apiParams,
      });

      let statesList = null;

      // If the region is a country, also fetch its states
      if (region.region_type === "country") {
        const res = await axios(`${config.API_URL}/api/v0/regions`, {
          params: {
            p: 1,
            limit: 1000,
            state: "active",
            methods: "parent_name",
            only: "name,canonical_name,region_type,id,images",
            ancestor_id: region.id,
            sort_key: "name",
            sort_order: "asc",
            region_type: "state",
            use_java_api: true,
          },
          ...config.apiParams,
        });
        statesList = res.data;
      }

      dispatch(
        getRegionListSuccess({ data, statesList, isCountry: region.region_type === "country" }),
      );

      // Fetch listing data (cities, areas, universities) for SSR
      try {
        const [citiesResult, areasResult, universitiesResult] = await Promise.allSettled([
          fetchStateLocalities(region.id, selectedLang),
          fetchStateAreas(region.id),
          fetchStateUniversities(region.id),
        ]);

        // Only keep fields the UI needs to minimize SSR payload size
        const pickListingFields = (item: any) => ({
          id: item.id,
          name: item.name,
          canonical_name: item.canonical_name,
          active_inventories_in_bound_count: item.active_inventories_in_bound_count,
        });

        const filterAndSlim = (result: PromiseSettledResult<any[]>) =>
          result.status === "fulfilled"
            ? result.value
                .filter((item: any) => item.active_inventories_in_bound_count > 0)
                .map(pickListingFields)
            : [];

        dispatch(
          updateListingData({
            cities: filterAndSlim(citiesResult),
            areas: filterAndSlim(areasResult),
            universities: filterAndSlim(universitiesResult),
          }),
        );
      } catch {
        // Non-critical: listing data fetch failure shouldn't break the page
      }

      const meta: any = {};
      // Meta
      meta.title_prefix = getSearchMetaTitle(region.meta, { region }, selectedLang);
      meta.description = getSearchMetaDescription(region.meta, { region }, selectedLang);
      meta.canonical_url_suffix = getSearchCanonicalUrl(canonical_name, { region });

      // Check for custom robots tag from region meta
      const customRobotsTag = getSearchRobotsTag({ region });

      if (customRobotsTag) {
        meta.robots = customRobotsTag;
      }

      meta.keywords = region.meta?.keywords
        ? region.meta?.keywords?.split(",").map((keyword: string) => keyword?.trim())
        : [];
      meta.showZhDescription = selectedLang === "zh";

      // Schema data for country and state pages
      const schemaData: any[] = [];

      // WebPage schema for country pages
      if (region.region_type === "country") {
        const pageUrl = urljoin(config.WEB_SERVICE_URL, getSearchLink(canonical_name));
        const aboutBlock: any = {
          "@type": "Country",
          name: region.name,
        };
        if (region?.meta?.same_as) aboutBlock.sameAs = region.meta.same_as;
        const webPageSchema: any = {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
          url: pageUrl,
          name: meta.title_prefix,
          about: aboutBlock,
        };
        schemaData.push(webPageSchema);
      }

      // WebPage schema for state/region page
      if (region.region_type === "state") {
        const pageUrl = urljoin(config.WEB_SERVICE_URL, getSearchLink(canonical_name));
        const countryAncestor = (region?.ancestors || region?.ancestors_short || []).find(
          (a: any) => a.region_type === "country",
        );
        const aboutBlock: any = {
          "@type": "State",
          name: region.name,
        };
        if (region?.meta?.same_as) aboutBlock.sameAs = region.meta.same_as;
        const webPageSchema: any = {
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${pageUrl}#webpage`,
          url: pageUrl,
          name: meta.title_prefix,
          about: aboutBlock,
        };
        if (countryAncestor) {
          const countryUrl = urljoin(
            config.WEB_SERVICE_URL,
            getSearchLink(countryAncestor.canonical_name),
          );
          webPageSchema.isPartOf = {
            "@type": "WebPage",
            "@id": `${countryUrl}#webpage`,
            url: countryUrl,
          };
          const containedIn: any = {
            "@type": "Country",
            name: countryAncestor.name,
          };
          if (countryAncestor?.meta?.same_as) containedIn.sameAs = countryAncestor.meta.same_as;
          webPageSchema.about.containedInPlace = containedIn;
        }
        schemaData.push(webPageSchema);
      }

      meta.schemaData = schemaData;
      dispatch(getMetaSuccess(meta));
    } catch (error) {
      dispatch(getRegionListError({ error }));
    }
  };
export default fetchRegionListData;
